import { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { ProjectPicker } from '../../components/ui/ProjectPicker';
import {
  documentService,
  Document,
  CreateDocumentData,
} from '../../services/document.service';
import { documentAdminService } from '../../services/document-admin.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

const DOC_TYPES = [
  { value: 'plano', label: 'Plano' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'permiso', label: 'Permiso' },
  { value: 'factura', label: 'Factura' },
  { value: 'reporte', label: 'Reporte' },
  { value: 'otro', label: 'Otro' },
] as const;

const CATEGORIES = [
  'Arquitectónico',
  'Estructural',
  'Eléctrico',
  'Hidrosanitario',
  'Legal',
  'Financiero',
  'Seguridad',
  'Calidad',
  'Otro',
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [projectId, setProjectId] = useState('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<string>('otro');
  const [formCategory, setFormCategory] = useState('Otro');
  const [isUploading, setIsUploading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);

  const canUpload = ['sys_admin', 'general_manager', 'project_lead', 'data_entry'].includes(
    user?.role ?? ''
  );
  const canToggleSignature = ['sys_admin', 'general_manager', 'project_lead'].includes(
    user?.role ?? ''
  );
  const canDelete = ['sys_admin', 'general_manager'].includes(user?.role ?? '');

  const loadDocs = () => {
    if (!projectId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError(null);
    documentService
      .getDocuments(projectId)
      .then(setDocuments)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDocs();
  }, [projectId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (filterType !== 'all' && d.type !== filterType) return false;
      if (!term) return true;
      return d.name.toLowerCase().includes(term);
    });
  }, [documents, search, filterType]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setFormName(file.name);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !formName.trim() || !projectId) {
      showError('Completa todos los campos');
      return;
    }
    setIsUploading(true);
    try {
      const data: CreateDocumentData = {
        project_id: projectId,
        name: formName.trim(),
        type: formType as Document['type'],
        category: formCategory,
        file: selectedFile,
      };
      await documentService.createDocument(data);
      showSuccess('Documento subido');
      setShowUpload(false);
      setSelectedFile(null);
      setFormName('');
      if (fileRef.current) fileRef.current.value = '';
      loadDocs();
    } catch (err: any) {
      showError(err.message || 'Error al subir');
    } finally {
      setIsUploading(false);
    }
  };

  const handleView = async (doc: Document) => {
    try {
      const { url } = await documentService.getDownloadUrl(doc.id);
      setViewerUrl(url);
    } catch (e: any) {
      showError(e.message || 'Error al abrir');
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { url } = await documentService.getDownloadUrl(doc.id);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
    } catch (e: any) {
      showError(e.message || 'Error al descargar');
    }
  };

  const handleToggleSignature = async (doc: Document) => {
    if (doc.signed) return;
    try {
      const next = !doc.requires_signature;
      await documentAdminService.toggleRequiresSignature(doc.id, next);
      showSuccess(next ? 'Requiere firma' : 'Ya no requiere firma');
      loadDocs();
    } catch (e: any) {
      showError(e.message || 'Error al actualizar');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!confirm(`¿Eliminar "${doc.name}"?`)) return;
    try {
      await documentService.deleteDocument(doc.id);
      showSuccess('Documento eliminado');
      loadDocs();
    } catch (e: any) {
      showError(e.message || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Documentos</h1>
          <p className="text-xs text-slate-500">{documents.length} en este proyecto</p>
        </div>
        {canUpload && projectId && (
          <button
            onClick={() => setShowUpload(true)}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
          >
            + Subir
          </button>
        )}
      </div>

      <ProjectPicker value={projectId} onChange={(id) => setProjectId(id)} />

      {projectId && (
        <>
          <div className="relative">
            <input
              type="search"
              placeholder="Buscar documento…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                filterType === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              Todos
            </button>
            {DOC_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setFilterType(t.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                  filterType === t.value
                    ? 'bg-primary text-white'
                    : 'bg-white text-slate-600 border border-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="📁"
              title={documents.length === 0 ? 'Sin documentos' : 'Sin resultados'}
              description={
                documents.length === 0
                  ? 'Sube el primer documento del proyecto.'
                  : 'Prueba con otro término o filtro.'
              }
            />
          ) : (
            <ul className="space-y-2">
              {filtered.map((doc) => (
                <li key={doc.id} className="bg-white rounded-xl shadow-sm p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-lg flex-shrink-0">
                      📄
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {doc.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatSize(doc.size)} · {doc.category}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <StatusBadge
                          status={doc.signed ? 'approved' : doc.requires_signature ? 'pending' : 'draft'}
                          label={
                            doc.signed
                              ? 'Firmado'
                              : doc.requires_signature
                              ? 'Requiere firma'
                              : 'Sin firma'
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                    <button
                      onClick={() => handleView(doc)}
                      className="flex-1 py-1.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                    >
                      Ver
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex-1 py-1.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                    >
                      Descargar
                    </button>
                    {canToggleSignature && !doc.signed && (
                      <button
                        onClick={() => handleToggleSignature(doc)}
                        className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${
                          doc.requires_signature
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {doc.requires_signature ? '✓ Firma' : 'Solicitar'}
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(doc)}
                        className="px-2 py-1.5 rounded-md bg-red-50 text-red-600 text-xs"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Subir documento</h2>
              <button
                onClick={() => setShowUpload(false)}
                className="text-slate-400 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpload} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Archivo *
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  onChange={handleFilePick}
                  required
                  className="w-full text-sm"
                />
                {selectedFile && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {selectedFile.name} ({formatSize(selectedFile.size)})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  >
                    {DOC_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Categoría
                  </label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUpload(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isUploading || !selectedFile}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {isUploading ? 'Subiendo…' : 'Subir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewerUrl && (
        <div
          className="fixed inset-0 z-50 bg-black flex flex-col"
          onClick={() => setViewerUrl(null)}
        >
          <div className="flex justify-between items-center px-4 py-3 bg-slate-900/80 text-white">
            <span className="text-sm">Visor</span>
            <button
              onClick={() => setViewerUrl(null)}
              className="w-9 h-9 rounded-full bg-white/20 text-white text-lg"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-slate-900">
            <iframe
              src={viewerUrl}
              title="Documento"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}